import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Compass, Users, Receipt, UserCheck, LifeBuoy, ShieldCheck, Search, MapPin, Quote, Star } from "lucide-react";
import { useSite } from "../../context/SiteContext";
import { api, img, money, fmtDate, expUrl, CATEGORIES } from "../../lib/api";
import { useSeo, pageSeo } from "../../lib/seo";
import { track } from "../../lib/analytics";
import { ExperienceCard, SectionHead, FaqList, Empty } from "../../components/site/Bits";
import { PostCard } from "./Blog";

function LatestPosts({ items }) {
  return (
    <section className="py-24 lg:py-32 bg-[#F5F5F5] border-t border-[#E5E5E5]" data-testid="blog-section">
      <div className="container-x">
        <SectionHead eyebrow="Stories & guides" title="Latest from the blog"><Link to="/blog" className="btn-secondary" data-testid="home-blog-all">Read all posts <ArrowRight className="h-4 w-4" /></Link></SectionHead>
        <div className="grid gap-6 md:grid-cols-3">{items.map((p, i) => <PostCard key={p.id} p={p} i={i} />)}</div>
      </div>
    </section>
  );
}

const WHY_ICONS = [Compass, UserCheck, Receipt, Users, LifeBuoy, ShieldCheck];

export default function Home() {
  const { site } = useSite();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/public/home").then((r) => setData(r.data)); }, []);
  const s = site?.sections || {};
  const org = site?.structured_data?.organization !== false && { "@context": "https://schema.org", "@type": "TravelAgency", name: site?.brand?.name || "Bhatkya", url: window.location.origin,
    ...(site?.contact?.email && { email: site.contact.email }), ...(site?.contact?.phone && { telephone: site.contact.phone }), sameAs: Object.values(site?.social || {}).filter(Boolean) };
  useSeo({ ...pageSeo(site, "home", { title: "Bhatkya - भटक्या | Treks, Tours, Heritage Walks, Camping & Corporate Treks", description: site?.hero?.subtext, ogImage: site?.hero?.image }), jsonLd: org }, [site]);
  if (!site) return <div className="h-screen bg-black" />;
  return (
    <>
      <Hero h={site.hero || {}} />
      {s.search !== false && <SearchBar />}
      {s.services !== false && <Services svc={site.services || {}} />}
      {s.featured !== false && <Featured items={data?.featured} />}
      {s.destinations !== false && data?.destinations?.length > 0 && <Destinations items={data.destinations} />}
      {s.why !== false && <Why why={site.why} />}
      {s.upcoming !== false && <Upcoming items={data?.upcoming} />}
      {s.corporate !== false && <CorporatePromo c={site.corporate || {}} />}
      {s.testimonials !== false && data?.testimonials?.length > 0 && <Testimonials items={data.testimonials} />}
      {s.blog !== false && data?.posts?.length > 0 && <LatestPosts items={data.posts} />}
      {s.faqs !== false && data?.faqs?.length > 0 && (
        <section className="py-24 lg:py-32 bg-white"><div className="container-x grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4"><p className="eyebrow mb-3">Questions</p><h2 className="section-title">Frequently asked</h2></div>
          <div className="lg:col-span-8"><FaqList faqs={data.faqs} testid="home-faqs" /></div>
        </div></section>
      )}
      {s.cta !== false && <FinalCta c={site.cta || {}} />}
    </>
  );
}

function Hero({ h }) {
  return (
    <section className="relative min-h-[88vh] flex items-end bg-black text-white on-dark overflow-hidden" data-testid="hero-section">
      <img src={img(h.image)} alt={h.image_alt || ""} fetchpriority="high" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-black/45" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
      <div className="relative container-x pb-20 lg:pb-28 w-full">
        <p className="eyebrow !text-white/70 mb-6 fade-up">Bhatkya · भटक्या · The Wanderer</p>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl font-bold tracking-tight leading-[0.95] max-w-5xl fade-up" data-testid="hero-headline">{h.headline}</h1>
        <p className="mt-8 max-w-xl text-base md:text-lg text-white/85 fade-up" style={{ animationDelay: "120ms" }} data-testid="hero-subtext">{h.subtext}</p>
        <div className="mt-10 flex flex-wrap gap-4 fade-up" style={{ animationDelay: "220ms" }}>
          <a href={h.primary_link || "#experiences"} className="btn-light" data-testid="hero-primary-btn">{h.primary_label}<ArrowRight className="h-4 w-4" /></a>
          <Link to={h.secondary_link || "/contact"} className="btn-ghost-light" data-testid="hero-secondary-btn">{h.secondary_label}</Link>
        </div>
      </div>
    </section>
  );
}

function SearchBar() {
  const nav = useNavigate();
  const [dests, setDests] = useState([]);
  const [f, setF] = useState({ destination: "", category: "", date: "", participants: "", budget: "" });
  useEffect(() => { api.get("/public/destinations").then((r) => setDests(r.data)); }, []);
  const go = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (f.destination) p.set("destination", f.destination);
    if (f.date) p.set("date", f.date);
    if (f.participants) p.set("participants", f.participants);
    if (f.budget) p.set("max_price", f.budget);
    track("search", { search_term: [f.category, f.destination].filter(Boolean).join(" ") });
    const base = f.category ? CATEGORIES.find((c) => c.key === f.category).path : "/experiences";
    nav(`${base}?${p.toString()}`);
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <section className="bg-[#F5F5F5] border-b border-[#E5E5E5]" data-testid="search-section">
      <form onSubmit={go} className="container-x -translate-y-10 lg:-translate-y-12">
        <div className="bg-white border border-black grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.5)]">
          <SF label="Destination"><select className="w-full bg-transparent text-sm focus:outline-none" value={f.destination} onChange={set("destination")} data-testid="search-destination"><option value="">Anywhere</option>{dests.map((d) => <option key={d.id} value={d.slug}>{d.name}</option>)}</select></SF>
          <SF label="Experience"><select className="w-full bg-transparent text-sm focus:outline-none" value={f.category} onChange={set("category")} data-testid="search-category"><option value="">All experiences</option>{CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</select></SF>
          <SF label="Travel date"><input type="date" min={new Date().toISOString().slice(0, 10)} className="w-full bg-transparent text-sm focus:outline-none" value={f.date} onChange={set("date")} data-testid="search-date" /></SF>
          <SF label="Participants"><input type="number" min="1" placeholder="Any" className="w-full bg-transparent text-sm focus:outline-none" value={f.participants} onChange={set("participants")} data-testid="search-participants" /></SF>
          <SF label="Budget / person"><select className="w-full bg-transparent text-sm focus:outline-none" value={f.budget} onChange={set("budget")} data-testid="search-budget"><option value="">Any budget</option><option value="1000">Up to ₹1,000</option><option value="2500">Up to ₹2,500</option><option value="5000">Up to ₹5,000</option><option value="10000">Up to ₹10,000</option></select></SF>
          <button type="submit" className="btn-primary h-full min-h-[64px] border-0" data-testid="search-submit"><Search className="h-4 w-4" />Search</button>
        </div>
      </form>
    </section>
  );
}
const SF = ({ label, children }) => <label className="block px-5 py-3 border-b md:border-r border-[#E5E5E5]"><span className="eyebrow block mb-1">{label}</span>{children}</label>;

function Services({ svc }) {
  return (
    <section id="experiences" className="pb-24 lg:pb-32 pt-8 bg-[#F5F5F5] scroll-mt-20" data-testid="services-section">
      <div className="container-x">
        <SectionHead eyebrow="Explore experiences" title="Five ways to wander." />
        <div className="grid gap-5 md:grid-cols-6">
          {CATEGORIES.map((c, i) => {
            const d = svc[c.key] || {};
            return (
              <Link key={c.key} to={c.path} data-testid={`service-card-${c.key}`} className={`group img-zoom relative overflow-hidden bg-black text-white on-dark ${i < 2 ? "md:col-span-3 aspect-[4/3]" : "md:col-span-2 aspect-[3/4]"}`}>
                <img src={img(d.image)} alt={d.title || c.label} loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-80" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
                  <p className="eyebrow !text-white/60">0{i + 1}</p>
                  <h3 className="font-display text-3xl lg:text-4xl font-bold mt-2">{d.title || c.label}</h3>
                  <p className="mt-3 text-sm text-white/80 max-w-sm">{d.short}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold border-b border-white pb-1">Explore <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Featured({ items }) {
  return (
    <section className="py-24 lg:py-32 bg-white" data-testid="featured-section">
      <div className="container-x">
        <SectionHead eyebrow="Featured" title="Handpicked experiences."><Link to="/experiences" className="btn-secondary">View all</Link></SectionHead>
        {items && !items.length ? <Empty title="No featured experiences yet" text="Featured experiences will appear here once published." /> : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">{(items || []).map((e, i) => <ExperienceCard key={e.id} e={e} i={i} />)}</div>
        )}
      </div>
    </section>
  );
}

function Destinations({ items }) {
  return (
    <section className="py-24 lg:py-32 bg-[#F5F5F5]" data-testid="destinations-section">
      <div className="container-x">
        <SectionHead eyebrow="Popular destinations" title="Where the trail leads."><Link to="/destinations" className="btn-secondary">All destinations</Link></SectionHead>
        <div className="grid gap-5 grid-cols-2 lg:grid-cols-4">
          {items.slice(0, 8).map((d, i) => (
            <Link key={d.id} to={`/destinations/${d.slug}`} className={`img-zoom group relative overflow-hidden bg-black ${i === 0 ? "col-span-2 row-span-2 aspect-square lg:aspect-auto" : "aspect-[4/5]"}`} data-testid={`destination-card-${d.slug}`}>
              <img src={img(d.image)} alt={d.image_alt || d.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-0 p-5 text-white"><p className="flex items-center gap-1 text-xs text-white/70"><MapPin className="h-3 w-3" />{d.region}</p><h3 className="font-display text-2xl font-bold">{d.name}</h3></div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function Why({ why }) {
  return (
    <section className="py-24 lg:py-32 bg-white" data-testid="why-section">
      <div className="container-x grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4"><p className="eyebrow mb-3">Why Bhatkya</p><h2 className="section-title">{why?.title || "Why Choose Us?"}</h2></div>
        <div className="lg:col-span-8 grid sm:grid-cols-2 border-t border-l border-[#E5E5E5]">
          {(why?.items || []).map((w, i) => {
            const Icon = WHY_ICONS[i % WHY_ICONS.length];
            return (
              <div key={i} className="p-8 border-r border-b border-[#E5E5E5] hover:bg-black hover:text-white transition-colors group">
                <Icon className="h-7 w-7" strokeWidth={1.5} />
                <h3 className="mt-6 text-lg font-bold">{w.title}</h3>
                <p className="mt-2 text-sm text-[#666] group-hover:text-white/75">{w.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Upcoming({ items }) {
  return (
    <section className="py-24 lg:py-32 bg-[#F5F5F5]" data-testid="upcoming-section">
      <div className="container-x">
        <SectionHead eyebrow="Calendar" title="Upcoming adventures." />
        {items && !items.length ? <Empty title="No upcoming departures" text="New dates are added regularly. Send us an enquiry to hear first." /> : (
          <div className="border-t border-black">
            {(items || []).map((d) => (
              <div key={d.id} className="grid grid-cols-2 md:grid-cols-12 gap-4 items-center py-6 border-b border-[#E5E5E5]" data-testid={`upcoming-row-${d.id}`}>
                <div className="md:col-span-2"><p className="font-display text-2xl font-bold">{fmtDate(d.start_date).split(" ").slice(0, 2).join(" ")}</p><p className="text-xs text-[#666]">{fmtDate(d.start_date).split(" ")[2]}</p></div>
                <div className="md:col-span-5 col-span-2 order-first md:order-none"><Link to={expUrl(d.experience)} className="text-lg font-semibold hover:underline">{d.experience.name}</Link><p className="text-xs text-[#666]">{d.experience.duration}</p></div>
                <div className="md:col-span-2 text-sm font-bold">{money(d.promo_price_minor || d.price_minor || d.experience.base_price_minor, d.experience.currency)}</div>
                <div className="md:col-span-1 text-xs">{d.status === "sold_out" ? <span className="chip-solid" data-testid="sold-out-badge">Sold out</span> : <span className="text-[#666]">{d.seats_left} left</span>}</div>
                <div className="md:col-span-2 md:text-right">
                  {d.status === "sold_out" ? <span className="btn-secondary btn-sm opacity-50 cursor-not-allowed">Sold out</span> :
                    <Link to={`${expUrl(d.experience)}?departure=${d.id}#book`} className="btn-primary btn-sm" onClick={() => track("click_booking_cta", { item_id: d.experience.id })} data-testid={`upcoming-book-${d.id}`}>Book</Link>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function CorporatePromo({ c }) {
  return (
    <section className="relative bg-black text-white on-dark overflow-hidden" data-testid="corporate-section">
      <div className="grid lg:grid-cols-2">
        <div className="container-x lg:pr-0 py-24 lg:py-32 flex flex-col justify-center">
          <p className="eyebrow !text-white/60 mb-4">Corporate</p>
          <h2 className="section-title text-white">{c.headline}</h2>
          <p className="mt-6 text-white/80 max-w-md">{c.subtext}</p>
          <Link to="/corporate-trek#proposal" className="btn-light mt-10 self-start" data-testid="corporate-proposal-btn">{c.button || "Request a Corporate Proposal"}</Link>
        </div>
        <div className="relative min-h-[360px]"><img src={img(c.image)} alt="Team on an outdoor trek" loading="lazy" className="absolute inset-0 h-full w-full object-cover" /></div>
      </div>
    </section>
  );
}

export function Testimonials({ items }) {
  return (
    <section className="py-24 lg:py-32 bg-white" data-testid="testimonials-section">
      <div className="container-x">
        <SectionHead eyebrow="Testimonials" title="From fellow wanderers." />
        <div className="grid gap-6 md:grid-cols-3">
          {items.map((t) => (
            <figure key={t.id} className="card p-8">
              <Quote className="h-6 w-6" />
              {t.rating && <div className="flex gap-0.5 mt-4" aria-label={`${t.rating} of 5`}>{Array.from({ length: t.rating }).map((_, i) => <Star key={i} className="h-4 w-4 fill-black" />)}</div>}
              <blockquote className="mt-4 text-[#333] leading-relaxed">{t.text}</blockquote>
              <figcaption className="mt-6 text-sm font-bold">{t.name}{t.experience_name && <span className="block font-normal text-[#666]">{t.experience_name}</span>}</figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCta({ c }) {
  return (
    <section className="py-24 lg:py-36 bg-[#F5F5F5] border-t border-[#E5E5E5]" data-testid="final-cta-section">
      <div className="container-x text-left md:flex items-end justify-between gap-10">
        <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight max-w-3xl leading-[0.95]">{c.headline}</h2>
        <div className="mt-10 md:mt-0 flex flex-wrap gap-4 shrink-0">
          <Link to={c.primary_link || "/trek"} className="btn-primary" data-testid="cta-explore-btn">{c.primary_label}</Link>
          <Link to={c.secondary_link || "/contact"} className="btn-secondary" data-testid="cta-contact-btn">{c.secondary_label}</Link>
        </div>
      </div>
    </section>
  );
}
